import {
  Box,
  Button,
  Divider,
  Grid,
  IconBadge,
  Link,
  Text,
} from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'
import { AnimatePresence, motion } from 'framer-motion'
import { SpendingPolicyFragment } from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import Callout from 'modules/core/components/Callout'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  categoryTemplateListExtended,
  customCategory,
} from 'modules/finance/lib/newSpendingPoliciesWizard'

import RemoveModal from './RemoveModal'
import RuleGroup from './RuleGroup'

type Props = {
  spendingPolicy: SpendingPolicyFragment
  defaultToggle: boolean
  editable: boolean
}

const PolicyContainer = ({ spendingPolicy, defaultToggle, editable }: Props) => {
  const [open, toggle] = useToggle(defaultToggle)
  const [modalOpen, toggleModal] = useToggle(false)
  const { navigation } = useNavigation()

  const categoryInfo =
    categoryTemplateListExtended().find((c) => c.icon === spendingPolicy.icon) || customCategory()

  const ruleGroups = spendingPolicy.spendingPolicyRuleGroupsConnection.nodes || []

  return (
    <Box background="grey100" borderRadius={{ all: 'abs016' }}>
      <Box flexDirection="row" justifyContent="spaceBetween" padding="s24">
        <Box
          gap="s12"
          flexDirection="row"
          textAlign="center"
          justifyContent="center"
          alignItems="center"
          onClick={toggle}
        >
          <IconBadge
            icon={categoryInfo.icon}
            size="s32"
            color={categoryInfo.color}
            iconColor={categoryInfo.badgeColor}
          />
          <Text.Header2 weight="semibold">{spendingPolicy.name}</Text.Header2>
        </Box>
        <Box flexDirection="row" gap="s16">
          {editable && (
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="start">
                <PopoverMenu.RouterOption
                  title={i18n.t('common.edit')}
                  to={navigation.expenses.spendingPolicies.detail.fullPath({
                    policyId: spendingPolicy.id,
                  })}
                />
                <PopoverMenu.TouchableOption
                  danger
                  title={i18n.t('common.delete')}
                  onClick={toggleModal}
                />
              </PopoverMenu.Content>
            </PopoverMenu>
          )}
          <Button
            type="secondary"
            onClick={toggle}
            iconName={open ? 'chevron-up' : 'chevron-down'}
          />
        </Box>
      </Box>
      <AnimatePresence>
        {!!open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box paddingX="s64" gap="s16" paddingBottom="s24">
              <Box flexDirection="row" flexWrap="wrap" gap="s12">
                {ruleGroups.length === 0 && (
                  <Box width="full">
                    <Callout
                      type="warning"
                      title={i18n.t('spending_policies.list.incomplete_policy.title')}
                      description=""
                      link={
                        <Link.Router
                          to={navigation.expenses.spendingPolicies.detail.fullPath({
                            policyId: spendingPolicy.id,
                          })}
                        >
                          {i18n.t('spending_policies.list.incomplete_policy.link')}
                        </Link.Router>
                      }
                    />
                  </Box>
                )}
                {ruleGroups.map((ruleGroup) => (
                  <Box width="full" key={ruleGroup.id}>
                    <Grid variant="large" key={ruleGroup.id}>
                      <Box gap="s4">
                        <RuleGroup ruleGroup={ruleGroup} categoryInfo={categoryInfo} />
                      </Box>
                    </Grid>
                    <Grid variant="large">
                      {ruleGroup.children.map((childRuleGroup) => (
                        <Box key={childRuleGroup.id} gap="s4" paddingY="s12">
                          <RuleGroup
                            parentRuleGroup={ruleGroup}
                            ruleGroup={childRuleGroup}
                            categoryInfo={categoryInfo}
                          />
                        </Box>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Box>
              {spendingPolicy.description && (
                <Box gap="s16">
                  <Divider />
                  <Text size="300" color="textSecondary" weight="normal">
                    {/* eslint-disable-next-line factorial/no-raw-html */}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(spendingPolicy.description),
                      }}
                    ></span>
                  </Text>
                </Box>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {modalOpen && <RemoveModal closeModal={toggleModal} spendingPolicy={spendingPolicy} />}
    </Box>
  )
}

export default PolicyContainer
